/**
 * The pure access decision behind {@link ProtectedRoute}, extracted so the
 * branching is unit-testable without mounting the component and its five hooks.
 *
 * The component owns the loading gate (it needs `hasResolvedOnce`); this function
 * assumes auth/role/org have resolved and returns the routing decision from the
 * last known values.
 *
 * Key rule (added for org editors): **access to the /admin/* org app is governed
 * by the caller's role in the ACTIVE organization, not by their global platform
 * role.** A platform admin who is only an editor of the organization they are
 * currently viewing gets editor access to it — the content/profile editors —
 * never the admin surfaces. RLS already blocks the data either way; this keeps
 * the UI honest and matches the reduced editor navigation.
 */

import { ADMIN_HOME } from "@/lib/postLoginDestination";

export type AppRole = "admin" | "agent" | null;

export interface RouteDecisionInput {
  hasUser: boolean;
  /** Platform role from user_roles. */
  role: AppRole;
  /** The caller's membership role in their ACTIVE org workspace, or null. */
  orgRole: string | null;
  /** Whether an active org has resolved. */
  hasOrg: boolean;
  requiredRole?: "admin" | "agent";
  /**
   * Org membership roles allowed on this route even without the platform
   * `requiredRole` — e.g. `["editor"]` on the profile editor.
   */
  allowOrgRoles?: string[];
  skipOrgCheck?: boolean;
  /**
   * The caller's own history (their reviews, their messages) on an agent
   * route: a platform admin with no readable organization may still open it,
   * because detaching or archiving their only office leaves the admin role
   * behind but removes the workspace (office profiles spec 17.6). It only
   * waives the setup redirect, only with `requiredRole: "agent"` and only in
   * the agent portal; it grants no organization or admin surface.
   */
  allowWithoutOrg?: boolean;
  isOrgAccount: boolean;
  isAgentPortalContext: boolean;
  pathname: string;
}

export type RouteDecision = { kind: "allow" } | { kind: "redirect"; to: string };

const allow: RouteDecision = { kind: "allow" };
const redirect = (to: string): RouteDecision => ({ kind: "redirect", to });

export function resolveRouteDecision(input: RouteDecisionInput): RouteDecision {
  const {
    hasUser,
    role,
    orgRole,
    hasOrg,
    requiredRole,
    allowOrgRoles,
    skipOrgCheck,
    allowWithoutOrg,
    isOrgAccount,
    isAgentPortalContext,
    pathname,
  } = input;

  if (!hasUser) {
    // Signed out: still home, but say why for the two account-gated agent
    // pages (#1051), so the landing page can open the matching sign-up
    // prompt. Access is unchanged: every signed-out visitor is redirected.
    if (pathname === "/feed") return redirect("/?signup=feed");
    if (pathname === "/compare" || pathname === "/agent/compare") return redirect("/?signup=compare");
    return redirect("/");
  }

  // A former office admin reading their own history needs no workspace.
  const personalHistoryWithoutOrg =
    allowWithoutOrg === true && requiredRole === "agent" && isAgentPortalContext;

  // Admin accounts without an org must complete setup first.
  if (role === "admin" && !skipOrgCheck && !hasOrg && !personalHistoryWithoutOrg && pathname !== "/admin/setup") {
    return redirect("/admin/setup");
  }

  // Org account_type users not yet promoted go to setup.
  if (isOrgAccount && role === "agent" && pathname !== "/admin/setup") {
    return redirect("/admin/setup");
  }

  // Active-org role governs the org app. Someone whose role in the active org is
  // only `editor` — INCLUDING a platform admin viewing an org they merely edit —
  // may reach a route only if it opts editors in; otherwise they go to the
  // editor's home (ADMIN_HOME, the profile editor), never the admin feed.
  if (requiredRole === "admin" && orgRole === "editor") {
    return allowOrgRoles?.includes("editor") ? allow : redirect(ADMIN_HOME);
  }

  if (requiredRole && role !== requiredRole) {
    // Dual-role admins in agent mode can use agent routes.
    if (role === "admin" && requiredRole === "agent" && isAgentPortalContext) {
      return allow;
    }
    // Preserved from the original: org accounts on a no-requiredRole route.
    // (Dead while requiredRole is truthy here, kept for parity.)
    if (isOrgAccount && !requiredRole) {
      return allow;
    }
    return redirect(role === "admin" ? ADMIN_HOME : "/feed");
  }

  return allow;
}
